import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button as AntButton,
  Modal,
  Select,
  Table,
  TableColumnsType,
} from 'antd';
import Button from '../Button';
import useAxios from '../../hooks/useAxios';
import {
  IEntranceExaminee,
  IGetEntranceExaminees,
} from '../../models/placeTest.model';
import { getEntranceExaminees } from '../../api/placeTest.api';
import Pagination from '../../components/Pagination';
import config from '../../lib/config';
import { getCommonCodeLabel } from '../../lib/store';
import moment from 'moment';
import _ from 'lodash';
import ErrorResponse from '../ErrorResponse';
import Loader from '../Loader';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducer';
import { useHistory } from 'react-router-dom';
const { Option } = Select;

interface IProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
}

const SearchExamineeModal: FC<IProps> = ({ visible, setVisible }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { currentCampus } = useSelector((state: RootState) => state.auth);
  const {
    data: res,
    isLoading,
    error,
    execute,
  } = useAxios<IGetEntranceExaminees>();

  const [data, setData] = useState<IEntranceExaminee[]>([]);
  const [field, setField] = useState('korName');
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (currentCampus) {
      execute(getEntranceExaminees(currentCampus));
    }
  }, [currentCampus]);

  useEffect(() => {
    if (res && !isLoading) {
      setData(res.data);
    }
  }, [res, isLoading]);

  const onSearch = () => {
    if (!res) return;

    let _data: IEntranceExaminee[] = _.cloneDeep(res.data);      
    _data = _data.filter((item: any) => item[field]?.toLowerCase().includes(keyword.toLowerCase()));

    setData(_data);
  };

  const columns: TableColumnsType<any> = [
    {
      title: t('entrance.schedule.column8'),
      dataIndex: 'korName',
      key: 'korName',
      align: 'center',
      sorter: (a, b) => a.korName?.localeCompare(b.korName),
      render: (value, record) => `${record.korName} (${record.engName})`,
    },
    {
      title: t('entrance.schedule.column9'),
      dataIndex: 'webId',
      key: 'webId',
      align: 'center',
      sorter: (a, b) => a.webId?.localeCompare(b.webId),
    },
    {
      title: t('entrance.schedule.column10'),
      dataIndex: 'schoolName',
      key: 'schoolName',
      align: 'center',
      sorter: (a, b) => a.schoolName?.localeCompare(b.schoolName),
    },
    {
      title: t('entrance.schedule.column11'),
      key: 'gradeCode',
      dataIndex: 'gradeCode',
      align: 'center',
      sorter: (a, b) => a.gradeCode?.localeCompare(b.gradeCode),
      render: (value) => getCommonCodeLabel('SY0006', value, 'ko'),
    },
    {
      title: t('entrance.schedule.column1'),
      key: 'examDate',
      dataIndex: 'examDate',
      align: 'center',
      sorter: (a, b) => a.examDate?.localeCompare(b.examDate),
      render: (value) => moment(value).format('YYYY.MM.DD'),
    },
    {
      title: t('entrance.schedule.column2'),
      key: 'srtTime',
      dataIndex: 'srtTime',
      align: 'center',
      sorter: (a, b) => a.srtTime?.localeCompare(b.srtTime),
    },
    {
      title: t('entrance.schedule.column12'),
      key: 'cancelYn',
      dataIndex: 'cancelYn',
      align: 'center',
      sorter: (a, b) => a.cancelYn?.localeCompare(b.cancelYn),
    },
  ];

  return (
    <>
      {isLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <Modal
        title={t('entrance.schedule.button1')}
        width={750}
        visible={visible}
        onCancel={() => setVisible(false)}
        footer={[
          <div className='comm-btn__area justify-content-center'>
            <AntButton key="cancel" className='btn-basic gray' onClick={() => setVisible(false)}>
              {t('common.button.close')}
            </AntButton>
          </div>
        ]}
      >
        <div>
          <div className="comm-bx__space">
            <div className="comm-btn__area right">
              <div className="w-150p">
                <Select
                  className="w-100"
                  placeholder={t('common.button.select')}
                  value={field}
                  onChange={(value) => setField(value)}
                >
                  <Option value="korName">{t('entrance.schedule.option1')}</Option>
                  <Option value="engName">{t('entrance.schedule.option2')}</Option>
                  <Option value="webId">ID</Option>
                </Select>
              </div>
              <input
                type="text"
                className="form-control w-240p"
                placeholder={t('common.text.input')}
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') onSearch();
                }}
              />
              <div className='comm-btn__area justify-content-center'>
                <Button
                  className="btn-basic small black"
                  onClick={onSearch}
                  text={t('common.button.search')}
                />
              </div>
            </div>
          </div>

          <div className='comm-table__area mt-0'>
            <Table
              columns={columns}
              dataSource={data || undefined}
              pagination={{
                current: page,
                pageSize: config.smallPageSize,
              }}
              onRow={(record) => {
                return {
                  onClick: () => {
                    history.push(`/entrance/schedule/${record.platScheduleCode}/examinee/${record.platRequireCode}`);
                  },
                };
              }}
              rowClassName="pointer"  
            />
          </div>
          <div className="comm-bx__space">
            <div className="common-paging center">
              <Pagination count={data.length} page={page} setPage={setPage} />
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default SearchExamineeModal;
